import { CircleAlertIcon } from '@persona-id/design-tokens/icons';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { DateTime } from 'luxon';
import { Dispatch, FC, SetStateAction, useState } from 'react';

import ToggleButton from 'dashboard/apps/InquiryTemplateEditor/ToggleButton';
import {
  LoadingShimmerParagraph,
  MaybeLoadingShimmer,
} from 'dashboard/components/LoadingShimmer/LoadingShimmer';
import Pii from 'dashboard/components/Pii';
import { FieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/FieldTemplate';
import ReadOnlyArrayFieldTemplate from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ReadOnlyArrayFieldTemplate';
import ReadOnlyBooleanWidget from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ReadOnlyBooleanWidget';
import { ReadOnlyObjectFieldTemplate } from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ReadOnlyObjectFieldTemplate';
import ReadOnlyTextWidget from 'dashboard/components/WorkflowControlPanel/StepConfigAction/rjsf/ReadOnlyTextWidget';
import CodeHighlight from 'dashboard/components-lib/CodeHighlight';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { ExternalIntegrationLog } from 'dashboard/models/external-integration-log';
import { useGetExternalIntegrationLogQuery } from 'dashboard/redux/api/external-integration-log';
import { useLazyGetUserQuery } from 'dashboard/redux/api/users';
import Attribute, { AttributeGrid } from 'design-system/components/Attribute';
import Card from 'design-system/components/Card/Card';
import Sticker from 'design-system/components/Sticker';
import { Label } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useOnMount from 'lib/hooks/useOnMount';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';

const Creator: FC<{
  data: NonNullable<ExternalIntegrationLog['relationships']['creator']['data']>;
}> = ({ data }) => {
  const [getUser, { data: { data: user } = { data: null }, isLoading, error }] =
    useLazyGetUserQuery();

  useOnMount(() => {
    if (data?.type === ApiModel.User) {
      getUser(data.id);
    }
  });

  if (data?.type === ApiModel.WorkflowRunStepAction) {
    return (
      <span css={{ overflow: 'clip' }}>
        <a href={`/dashboard/workflow-steps/${data.id}`}>{data.id}</a>
      </span>
    );
  } else {
    let userName: string;
    if (isLoading) {
      return <LoadingShimmerParagraph height='36px' width='75%' />;
    } else if (error || !(user?.attributes['name-first'] || user?.attributes['name-last'])) {
      userName = data.id;
    } else {
      userName = `${user?.attributes['name-first']} ${user?.attributes['name-last']}`;
    }
    return <span css={{ overflow: 'clip' }}>{userName}</span>;
  }
};

function presentTextBlob(textBlob: string): string {
  try {
    const jsonBlob = JSON.parse(textBlob || '{}');
    return JSON.stringify(jsonBlob, null, 2);
  } catch (e) {
    return textBlob;
  }
}

const HttpLogSection: FC<{
  title: string;
  statusCode?: string;
  schema: any;
  logData: string;
  isLoading: boolean;
  showJson: boolean;
  setShowJson: Dispatch<SetStateAction<boolean>>;
}> = ({ title, statusCode, schema, logData, isLoading, showJson, setShowJson }) => {
  const isSuccess = statusCode === '200' || statusCode === '201';

  return (
    <Card>
      <Card.Body>
        <Card.Title>{title}</Card.Title>

        <ToggleButton
          inactiveButtonLabel='Show Form'
          activeButtonLabel='Show Raw JSON'
          setIsActive={setShowJson}
          isActive={showJson}
        />

        {showJson ? (
          <MaybeLoadingShimmer isLoading={isLoading} height='192px' width='auto'>
            <Pii isRedacted={false}>
              <CodeHighlight
                id={`integration-log-show__${title.toLowerCase().replace(' ', '_')}`}
                language='json'
                rowsToShow={20}
              >
                {presentTextBlob(logData || '{}')}
              </CodeHighlight>
            </Pii>
          </MaybeLoadingShimmer>
        ) : schema ? (
          <>
            {statusCode ? (
              <div css={{ marginBottom: '5px' }}>
                <Label size='md' weight='bold'>
                  Response status code:
                </Label>{' '}
                <Sticker
                  variant={isSuccess ? 'green' : 'red'}
                  aria-label={isSuccess ? 'success' : 'error'}
                >
                  {statusCode}
                </Sticker>
              </div>
            ) : null}

            <Form
              schema={schema.schema}
              uiSchema={{
                ...schema.uiSchema,
                'ui:submitButtonOptions': { norender: true },
                'ui:readonly': true,
              }}
              formData={JSON.parse(logData || '{}')}
              validator={validator}
              templates={{
                FieldTemplate,
                ObjectFieldTemplate: ReadOnlyObjectFieldTemplate,
                ArrayFieldTemplate: ReadOnlyArrayFieldTemplate,
              }}
              widgets={{
                TextWidget: ReadOnlyTextWidget,
                DateWidget: ReadOnlyTextWidget,
                DateTimeWidget: ReadOnlyTextWidget,
                CheckboxWidget: ReadOnlyBooleanWidget,
              }}
            />
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
};

const PREFIXES = {
  INTEGRATION_CREDENTIAL: 'eic',
  WORKFLOW_RUN: 'wfr',
  WORKFLOW: 'wfl',
  WORKFLOW_VERSION: 'wfv',
};

const SLUG_TO_ROUTE: Record<string, string> = {
  wfr: `/dashboard/workflow-runs`,
  wfl: `/dashboard/workflows`,
};

const linkToEntity = (id: string) => {
  const prefix = id.split('_')[0];
  const route = SLUG_TO_ROUTE[prefix];
  return route ? `${route}/${id}` : null;
};

const groupRelatedObjects = (relatedObjectIds: string[]) => {
  const grouped = {
    integrationCredentials: [] as string[],
    workflowRuns: [] as string[],
    workflows: [] as string[],
    workflowVersions: [] as string[],
    others: [] as string[],
  };

  relatedObjectIds.forEach((id) => {
    const prefix = id.split('_')[0];
    switch (prefix) {
      case PREFIXES.INTEGRATION_CREDENTIAL:
        grouped.integrationCredentials.push(id);
        break;
      case PREFIXES.WORKFLOW_RUN:
        grouped.workflowRuns.push(id);
        break;
      case PREFIXES.WORKFLOW:
        grouped.workflows.push(id);
        break;
      case PREFIXES.WORKFLOW_VERSION:
        grouped.workflowVersions.push(id);
        break;
      default:
        grouped.others.push(id);
        break;
    }
  });

  return grouped;
};

const IntegrationLog: FC<{ token: string }> = ({ token }) => {
  const {
    data: { data: log, meta: schema } = {},
    isLoading,
    error,
  } = useGetExternalIntegrationLogQuery(token);

  const showRJSFOption = useFeatureFlag('dashboard:enable-rjsf-integration-log');

  useRTKErrorHandler({
    error,
    onError: (error) => ({
      message: error?.errors?.at(0)?.title || 'An error occurred loading the Integration Log',
      level: MessageType.Failure,
    }),
  });

  const [showJsonRequest, setShowJsonRequest] = useState(false);
  const [showJsonResponse, setShowJsonResponse] = useState(false);

  if (!isLoading && log == null) {
    return (
      <Card>
        <Card.Body css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <CircleAlertIcon />
          <p>{`Integration Log `}</p>
          <pre>{token}</pre>
          <p>{` not found.`}</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <Card.Body>
          <Card.Title>{schema?.title}</Card.Title>
          <MaybeLoadingShimmer isLoading={isLoading} height='192px' width='100%'>
            <AttributeGrid>
              <Attribute
                label='Operation'
                key='Operation'
                value={log?.attributes.integration?.['operation-id']}
                placeholder=''
              />
              <Attribute
                label='Status'
                key='Status'
                value={log?.attributes.status}
                placeholder=''
              />
              {log?.relationships.creator.data != null || isLoading ? (
                <Attribute
                  label='Creator'
                  key='Creator'
                  value={
                    log?.relationships.creator.data ? (
                      <Creator data={log.relationships.creator.data} />
                    ) : null
                  }
                  placeholder=''
                />
              ) : null}
              <Attribute
                label='Created At'
                key={`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
                value={
                  log?.attributes['created-at']
                    ? DateTime.fromISO(log.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)
                    : 'N/A'
                }
                placeholder=''
              />
              <Attribute
                label='Updated At'
                key={`Updated at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
                value={
                  log?.attributes['created-at']
                    ? DateTime.fromISO(log.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)
                    : 'N/A'
                }
                placeholder=''
              />

              {Object.entries(groupRelatedObjects(log?.attributes['related-object-ids'] || []))
                .filter(([_, ids]) => ids.length)
                .map(([key, ids]) => (
                  <Attribute
                    key={key}
                    label={key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (char) => char.toUpperCase())}
                    value={
                      <div>
                        {ids.map((id) => {
                          const url = linkToEntity(id);
                          return url ? (
                            <div key={id}>
                              <a href={url} target='_blank' rel='noopener noreferrer'>
                                {id}
                              </a>
                            </div>
                          ) : (
                            <div key={id}>{id}</div>
                          );
                        })}
                      </div>
                    }
                    placeholder=''
                  />
                ))}
            </AttributeGrid>
          </MaybeLoadingShimmer>
        </Card.Body>
      </Card>

      {(isLoading || log?.attributes.request) &&
        (showRJSFOption && schema?.request?.schema ? (
          <HttpLogSection
            title='HTTP Request'
            schema={schema?.request}
            logData={log?.attributes.request || '{}'}
            isLoading={isLoading}
            showJson={showJsonRequest}
            setShowJson={setShowJsonRequest}
          />
        ) : (
          <Card>
            <Card.Body>
              <Card.Title>HTTP Request</Card.Title>
              <MaybeLoadingShimmer isLoading={isLoading} height='192px' width='auto'>
                <Pii isRedacted={false}>
                  <CodeHighlight id='integration-log-show__request' language='json' rowsToShow={20}>
                    {presentTextBlob(log?.attributes.request || '{}')}
                  </CodeHighlight>
                </Pii>
              </MaybeLoadingShimmer>
            </Card.Body>
          </Card>
        ))}

      {(isLoading || log?.attributes.response) &&
        (showRJSFOption && schema?.response?.schema ? (
          <HttpLogSection
            title='HTTP Response'
            statusCode={log?.attributes['response-status-code'] || ''}
            schema={schema?.response}
            logData={log?.attributes.response || '{}'}
            isLoading={isLoading}
            showJson={showJsonResponse}
            setShowJson={setShowJsonResponse}
          />
        ) : (
          <Card>
            <Card.Body>
              <Card.Title>HTTP Response</Card.Title>
              <MaybeLoadingShimmer isLoading={isLoading} height='192px' width='auto'>
                <Pii isRedacted={false}>
                  <CodeHighlight
                    id='integration-log-show__response'
                    language='json'
                    rowsToShow={20}
                  >
                    {presentTextBlob(log?.attributes.response || '{}')}
                  </CodeHighlight>
                </Pii>
              </MaybeLoadingShimmer>
            </Card.Body>
          </Card>
        ))}

      {(isLoading || log?.attributes['response-headers']) && (
        <Card>
          <Card.Body>
            <Card.Title>HTTP Response headers</Card.Title>
            <MaybeLoadingShimmer isLoading={isLoading} height='192px' width='auto'>
              <Pii isRedacted={false}>
                <CodeHighlight id='integration-log-show__headers' language='json' rowsToShow={20}>
                  {presentTextBlob(log?.attributes['response-headers'] || '{}')}
                </CodeHighlight>
              </Pii>
            </MaybeLoadingShimmer>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default IntegrationLog;
